import React from "react";
import { reviewAggregationToolBarId } from "../ReviewAggregationToolbar";
import { CustomerReviews, FilteredBy } from "../../reviewModels";
import ReviewsStarBreakdownContent from "../ReviewsStarBreakdownContent";

interface ReviewStarBreakdownProps {
  reviews: CustomerReviews;
  isProductReviewPage?: boolean;
  filteredBy?: FilteredBy;
  onRatingClick: (rating: number) => void;
}

export interface RatingCounts {
  reviewCount: number;
  rating: number;
}

const ReviewsStarBreakdownV2 = ({
  reviews,
  isProductReviewPage,
  filteredBy,
  onRatingClick,
}: ReviewStarBreakdownProps) => {
  const ratingClickHandler = (rating: number) => {
    onRatingClick(rating);
  };

  const ratingCounts = (() => {
    const counts: RatingCounts[] = [];
    for (let starRating = 5; starRating > 0; starRating -= 1) {
      counts.push({
        rating: starRating,
        reviewCount: reviews.productReviewDetails.filter(
          (review) => review.oneToFiveStarRating === starRating,
        ).length,
      });
    }

    return counts;
  })();

  const filteredByRating = filteredBy?.rating
    ? parseInt(filteredBy.rating, 10)
    : undefined;

  const getContainerClass = () => {
    const base = "review-star-breakdown py-0 border-gray-200 border-solid";

    const productPage = "w-full md:w-1/2";
    const productPageClass = `border-t-0 lt:flex lt:justify-center lt:items-center lt:py-6 lt:border-r-0 ${productPage}`;

    const nonProductPageClass = "xxxsl:border-r px-5";

    const filterClass =
      "w-full md:w-1/2 border border-solid border-l-0 md:border-l border-b-0 border-r-0 border-t-0 xxxsl:m-0 xxxsl:flex xxxsl:justify-center xxxsl:items-center";

    let summaryClass = "";

    if (!isProductReviewPage) {
      summaryClass = "md:w-1/2 xxxsl:w-1/3";
    } else if (!reviews.summary) {
      summaryClass = "xxxl:w-2/3";
    } else {
      summaryClass = "xl:w-1/2 xxxsl:w-2/3 m-0";
    }

    return `${base} ${
      isProductReviewPage ? productPageClass : nonProductPageClass
    } ${filterClass} ${summaryClass}`;
  };

  const getInnerContainerClass = () => {
    let base = "";
    if (isProductReviewPage) {
      base = "py-6 box-border pl-5 pr-3 xs:pr-0 xs:pl-1";
    } else {
      base = "py-5 xxxsl:py-0";
    }
    return `${base} w-full flex flex-col`;
  };

  const getRatingButtonClass = (rating: number) => {
    const selected = filteredByRating === rating;
    return `flex w-full bg-transparent items-center group cursor-pointer hover:no-underline py-[3px] pl-1 pr-0.5 border-2 border-solid rounded focus:outline-blue-700 ${
      selected ? "font-bold border-green-500" : "underline border-transparent"
    }`;
  };

  return (
    <div className={getContainerClass()}>
      <div className={getInnerContainerClass()}>
        <ul className="list-none p-0 m-0 flex flex-col">
          {ratingCounts.map((ratingCount) => (
            <li key={ratingCount.rating}>
              {/* for larger screens (remove jumplink) */}
              <button
                type="button"
                className={`hidden sm:flex w-full bg-transparent items-center group cursor-pointer hover:no-underline py-[3px] pl-1 pr-0.5 border-2 border-solid rounded focus:outline-blue-700 ${
                  filteredByRating && filteredByRating === ratingCount.rating
                    ? "font-bold border-green-500"
                    : "underline border-transparent"
                }`}
                onClick={() => ratingClickHandler(ratingCount.rating)}
                data-testid={`review-rating-${ratingCount.rating}`}
              >
                <ReviewsStarBreakdownContent
                  ratingCount={ratingCount}
                  filteredByRating={filteredByRating}
                  reviews={reviews}
                />
              </button>
              {/* for smaller screens (keep jumplink) */}
              <a
                className={`sm:hidden ${getRatingButtonClass(ratingCount.rating)}`}
                href={`#${reviewAggregationToolBarId}`}
                onClick={() => ratingClickHandler(ratingCount.rating)}
                data-testid={`review-rating-${ratingCount.rating}`}
              >
                <ReviewsStarBreakdownContent
                  ratingCount={ratingCount}
                  filteredByRating={filteredByRating}
                  reviews={reviews}
                />
              </a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default ReviewsStarBreakdownV2;
